import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import { Loader } from 'components/Widget/Loader';
import { DesignSystem, DesignSystemVersion } from 'dragonmouse-shared/interfaces';
import { useEffect, useRef, useState } from 'react';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { useSelector } from 'react-redux';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import LinkPanel from './LinkPanel';
import ActiveDsPanel from './ActiveDsPanel';
import * as S from './styles';

export interface DsPickerSelection {
	id: string;
	status: 'latest' | 'publishing' | 'updateAvailable' | null;
	linkedVersion?: DesignSystemVersion | null;
	latestVersion?: DesignSystemVersion | null;
}

interface DsPickerProps {
	selection?: DsPickerSelection | null;
	designSystems: DesignSystem[];
	compactMode?: 'full' | 'compact'; // 'compact' renders this widget at minimum width (hides text, etc)
	onSelect: (designSystem: DesignSystem | null) => Promise<void>;
	onCreateNew: () => void;
	onUpgrade?: () => Promise<void>;
}

const DsPicker = ({
	selection,
	designSystems,
	compactMode = 'full',
	onSelect,
	onCreateNew,
	onUpgrade,
}: DsPickerProps) => {
	const theme = useTheme();
	const isMobile = useSelector((r: RootReducer) => selectIsMobile(r.mode));
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [showLinkPanel, setShowLinkPanel] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [tooltipId] = useState(() => `ds-picker-tooltip-${Math.random()}`);
	const [dropdownPosition, setDropdownPosition] = useState<'above' | 'below' | 'center'>('above');

	const hasActiveDs = !!selection;
	const activeDesignSystem = selection ? designSystems.find((ds) => ds.id === selection.id) : null;
	const status = selection?.status ?? null;
	const hasVersionInfo =
		selection?.linkedVersion !== undefined && selection?.latestVersion !== undefined;

	// Close dropdown when clicking outside
	useOnClickOutside(dropdownRef, () => {
		if (isDropdownOpen) {
			setIsDropdownOpen(false);
			setShowLinkPanel(false);
		}
	});

	// Close dropdown when pressing Escape
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isDropdownOpen) {
				setIsDropdownOpen(false);
				setShowLinkPanel(false);
				// Remove focus from button to prevent outline
				if (buttonRef.current) {
					buttonRef.current.blur();
				}
			}
		};

		window.addEventListener('keydown', handleEscape);
		return () => {
			window.removeEventListener('keydown', handleEscape);
		};
	}, [isDropdownOpen]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const calculateDropdownPosition = () => {
		if (!buttonRef.current) return 'above';

		const buttonRect = buttonRef.current.getBoundingClientRect();
		const viewportHeight = window.innerHeight;
		const dropdownHeight = 454; // Approximate height of dropdown (can be adjusted)
		const gap = 8; // Gap between button and dropdown

		const spaceAbove = buttonRect.top;
		const spaceBelow = viewportHeight - buttonRect.bottom;

		// If we can fit above without overflowing, prefer above
		if (spaceAbove >= dropdownHeight + gap) {
			return 'above';
		}
		// If we can fit below without overflowing, use below
		if (spaceBelow >= dropdownHeight + gap) {
			return 'below';
		}
		// If neither fits well, center it (split overflow)
		return 'center';
	};

	const onToggleDropdown = () => {
		const newIsOpen = !isDropdownOpen;
		if (newIsOpen) {
			// Calculate position when opening
			setDropdownPosition(calculateDropdownPosition());
		}
		setIsDropdownOpen(newIsOpen);
		// Reset link panel visibility when closing dropdown
		if (!newIsOpen) {
			setShowLinkPanel(false);
		}
	};

	const handleSelect = async (designSystem: DesignSystem | null) => {
		// Close dropdown immediately for better UX
		setIsDropdownOpen(false);
		setShowLinkPanel(false);
		setIsLoading(true);

		try {
			await onSelect(designSystem);
		} finally {
			setIsLoading(false);
		}
	};

	// Determine when to show tooltip:
	// 1. Empty state: Show tooltip to guide user to add first design system
	// 2. Compact mode: Show tooltip with design system title since text is hidden
	const shouldShowTooltip = !isLoading && (!hasActiveDs || compactMode === 'compact');
	const tooltipContent = !hasActiveDs
		? 'Click to add first design system'
		: compactMode === 'compact' && activeDesignSystem
		? activeDesignSystem.title
		: undefined;

	return (
		<S.DropdownContainer ref={dropdownRef}>
			<S.DsControl
				ref={buttonRef}
				isEmpty={!hasActiveDs}
				isActive={hasActiveDs}
				isLoading={status === 'publishing' || isLoading}
				compactMode={compactMode}
				onClick={onToggleDropdown}
				data-tooltip-id={shouldShowTooltip ? tooltipId : undefined}
				data-tooltip-content={tooltipContent}
			>
				{isLoading ? (
					<>
						<Loader width={12} height={12} />
						<S.DsText>{'updating...'}</S.DsText>
					</>
				) : hasActiveDs ? (
					<>
						{status === 'latest' || status === null ? (
							<S.DsStatusContainer>
								<Icon type={IconType.STACK} width={14} height={14} />
							</S.DsStatusContainer>
						) : null}
						{status === 'publishing' && <Loader width={12} height={12} />}
						{status === 'updateAvailable' && (
							<S.DsStatusContainer>
								<Icon type={IconType.REFRESH} width={12} height={12} color={theme.blue[500]} />
							</S.DsStatusContainer>
						)}
						{compactMode === 'full' && (
							<S.DsText>{activeDesignSystem?.title || 'Design System'}</S.DsText>
						)}
						<S.DsChevron>
							<Icon
								type={IconType.FILLED_ARROW_DOWN}
								width={10}
								height={10}
								color={theme.gray[600]}
							/>
						</S.DsChevron>
					</>
				) : (
					<>
						<S.DsIcon>
							<Icon type={IconType.STACK} width={14} height={14} />
						</S.DsIcon>
						{compactMode === 'full' ? (
							<S.DsText>Add DS</S.DsText>
						) : (
							<Icon type={IconType.ADD} width={10} height={10} />
						)}
					</>
				)}
			</S.DsControl>
			{shouldShowTooltip && <Tooltip id={tooltipId} placement="top" />}
			<S.Dropdown isOpen={isDropdownOpen && !isLoading} position={dropdownPosition}>
				{hasActiveDs && !showLinkPanel && hasVersionInfo && selection ? (
					<ActiveDsPanel
						selection={selection}
						designSystems={designSystems}
						isMobile={isMobile}
						onSelect={handleSelect}
						onUpgrade={onUpgrade}
						onClose={() => setIsDropdownOpen(false)}
						onShowLinkPanel={() => setShowLinkPanel(true)}
					/>
				) : (
					<LinkPanel
						designSystems={designSystems}
						onCreateNew={onCreateNew}
						onLinkDs={handleSelect}
					/>
				)}
			</S.Dropdown>
		</S.DropdownContainer>
	);
};

export default DsPicker;
